import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
// import { useNavigate } from "react-router-dom";

import { useGetReturns } from "../../hooks/returns/useGetReturns";
import useTableActions from "../../hooks/common/useTableActions";

import TableLayout from "../global/Table/TableLayout";
import { ReturnsTableColumns } from "./Table/ReturnsTableColumns";
import ReturnsTableRow from "./Table/ReturnsTableRow";
import { RETURN_STATUS_META } from "./Table/ReturnStatusBadge";

const ReturnsTable = () => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || "en").startsWith("ar");
    // const navigate = useNavigate();

    const [statusFilter, setStatusFilter] = useState("");
    const [refundStatusFilter, setRefundStatusFilter] = useState("");

    const {
        page,
        perPage,
        search,
        order,
        selectedIds,
        menuAnchorEl,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleView,
        handlePageChange,
        handlePerPageChange,
    } = useTableActions({
        basePath: "/returns",
    });

    const params = useMemo(() => ({
        page,
        per_page: perPage,
        search: search || undefined,
        status: statusFilter || undefined,
        refund_status: refundStatusFilter || undefined,
        sort_by: "created_at",
        sort_order: order,
    }), [page, perPage, search, order, statusFilter, refundStatusFilter]);

    const { returns, meta, isLoading, error, refetch } = useGetReturns(params);

    const statusOptions = useMemo(() => ([
        { value: "", label: { ar: "الكل", en: "All" } },
        ...Object.entries(RETURN_STATUS_META).map(([value, meta]) => ({
            value,
            label: { ar: meta.labelAr, en: meta.labelEn },
        })),
    ]), []);

    const refundStatusOptions = useMemo(() => ([
        { value: "", label: { ar: "الكل", en: "All" } },
        { value: "pending", label: { ar: "قيد المعالجة", en: "Pending" } },
        { value: "completed", label: { ar: "مكتمل", en: "Completed" } },
        { value: "failed", label: { ar: "فشل", en: "Failed" } },
    ]), []);

    const tableFilters = [
        {
            name: "status",
            label: { ar: "الحالة", en: "Status" },
            value: statusFilter,
            onChange: setStatusFilter,
            options: statusOptions,
        },
        {
            name: "refund_status",
            label: { ar: "حالة الاسترداد", en: "Refund Status" },
            value: refundStatusFilter,
            onChange: setRefundStatusFilter,
            options: refundStatusOptions,
        },
    ];

    const renderRow = (rowParams) => (
        <ReturnsTableRow
            key={rowParams.item.id}
            item={rowParams.item}
            index={rowParams.index}
            isSelected={selectedIds.includes(rowParams.item.id)}
            onRowCheckboxClick={handleRowCheckboxClick}
            onMenuOpen={handleMenuOpen}
        />
    );

    return (
        <TableLayout
            title={isArabic ? "المرتجعات" : "Returns"}
            subtitle={isArabic ? "إدارة طلبات الإرجاع وعمليات الاسترداد" : "Manage return requests and refunds"}
            search={search}
            onSearchChange={setSearch}
            addButtonVisible={false}
            isLoading={isLoading}
            error={error}
            items={returns}
            meta={meta}
            page={page}
            perPage={perPage}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
            order={order}
            onRequestSort={handleRequestSort}
            columns={ReturnsTableColumns}
            renderRow={renderRow}
            menuAnchorEl={menuAnchorEl}
            openMenu={Boolean(menuAnchorEl)}
            onMenuOpen={handleMenuOpen}
            onMenuClose={handleMenuClose}
            onMenuView={handleView}
            menuDisableEdit
            menuDisableDelete
            selectedIds={selectedIds}
            onSelectAllClick={(e) => handleSelectAllClick(e, returns)}
            onRowCheckboxClick={handleRowCheckboxClick}
            bulkDeleteDisabled
            filters={tableFilters}
            onClearAllFilters={() => {
                setStatusFilter("");
                setRefundStatusFilter("");
            }}
            emptyLabelAr="لا توجد طلبات إرجاع"
            emptyLabelEn="No returns found"
            refetch={refetch}
        />
    );
};

export default ReturnsTable;
